import React from 'react';
import { ExternalLink, ShoppingCart, Star, AlertCircle } from 'lucide-react';

interface AmazonProductCardProps {
  product: {
    id: string;
    asin: string;
    title: string;
    description?: string;
    price?: string;
    imageUrl?: string;
    affiliateUrl?: string;
    category?: string;
    brand?: string;
    features?: string[];
  };
  size?: 'small' | 'medium' | 'large';
  showFeatures?: boolean;
  className?: string;
}

export function AmazonProductCard({
  product,
  size = 'medium',
  showFeatures = true,
  className = ''
}: AmazonProductCardProps) {
  const handleProductClick = () => {
    // Track affiliate link clicks for analytics
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'affiliate_click', {
        event_category: 'Amazon Affiliate',
        event_label: product.asin,
        value: product.price ? parseFloat(product.price.replace(/[^0-9.,]/g, '').replace(',', '.')) : 0
      });
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return {
          container: 'max-w-sm',
          image: 'w-24 h-24',
          title: 'text-sm',
          description: 'text-xs',
          price: 'text-sm',
          button: 'text-xs px-3 py-1.5'
        };
      case 'large':
        return {
          container: 'max-w-2xl',
          image: 'w-40 h-40',
          title: 'text-lg',
          description: 'text-sm',
          price: 'text-xl',
          button: 'text-sm px-4 py-2'
        };
      default: // medium
        return {
          container: 'max-w-xl',
          image: 'w-32 h-32',
          title: 'text-base',
          description: 'text-sm',
          price: 'text-lg',
          button: 'text-sm px-3 py-2'
        };
    }
  };

  const sizeClasses = getSizeClasses();

  return (
    <div className={`amazon-product-card bg-card border border-border rounded-lg p-3 sm:p-4 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 group ${sizeClasses.container} ${className}`}>
      <div className="flex gap-3 sm:gap-4">
        {/* Product Image */}
        <div className={`${sizeClasses.image} bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex-shrink-0 relative`}>
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.title}
              className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-gray-400" />
            </div>
          )}

          {/* Brand Badge */}
          {product.brand && (
            <div className="absolute top-1 left-1">
              <div className="bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                {product.brand}
              </div>
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            {/* Category */}
            {product.category && (
              <div className="text-xs text-muted-foreground mb-1">
                {product.category}
              </div>
            )}

            {/* Title */}
            <h3 className={`${sizeClasses.title} font-medium text-foreground line-clamp-2 mb-1 group-hover:text-orange-600 transition-colors`}>
              {product.title}
            </h3>
            
            {/* Description */}
            {product.description && size !== 'small' && (
              <p className={`${sizeClasses.description} text-muted-foreground line-clamp-2 mb-1`}>
                {product.description}
              </p>
            )}

            {/* Price */}
            {product.price && (
              <div className={`${sizeClasses.price} font-bold text-orange-600 mb-2`}>
                {product.price}
              </div>
            )}
          </div>

          {/* CTA Button */}
          <a
            href={product.affiliateUrl || `https://www.amazon.es/dp/${product.asin}`}
            target="_blank"
            rel="noopener noreferrer sponsored"
            onClick={handleProductClick}
            className={`bg-orange-500 hover:bg-orange-600 text-white font-medium rounded-md transition-colors duration-200 flex items-center justify-center space-x-1 sm:space-x-2 group/btn ${sizeClasses.button} self-start`}
          >
            <span className="inline">Cómpralo en Amazon</span>
            <ExternalLink className="h-3 w-3 group-hover/btn:translate-x-0.5 transition-transform" />
          </a>
        </div>
      </div>
    </div>
  );
}

// Compact version for inline use
export function AmazonProductCardCompact({ product, className = '' }: {
  product: AmazonProductCardProps['product'];
  className?: string;
}) {
  const handleProductClick = () => {
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'affiliate_click', {
        event_category: 'Amazon Affiliate',
        event_label: product.asin,
        value: product.price ? parseFloat(product.price.replace(/[^0-9.,]/g, '').replace(',', '.')) : 0
      });
    }
  };

  return (
    <div className={`amazon-product-card-compact bg-card border border-border rounded-lg p-3 hover:shadow-md transition-all duration-200 group ${className}`}>
      <div className="flex items-center space-x-3">
        {/* Product Image */}
        <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden flex-shrink-0">
          {product.imageUrl ? (
            <img
              src={product.imageUrl}
              alt={product.title}
              className="w-full h-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-gray-400" />
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground line-clamp-1 group-hover:text-orange-600 transition-colors">
            {product.title}
          </h4>
          
          {product.price && (
            <div className="text-sm font-bold text-orange-600 mt-1">
              {product.price}
            </div>
          )}
        </div>

        {/* CTA Button */}
        <a
          href={product.affiliateUrl || `https://www.amazon.es/dp/${product.asin}`}
          target="_blank"
          rel="noopener noreferrer sponsored"
          onClick={handleProductClick}
          className="bg-orange-500 hover:bg-orange-600 text-white text-xs px-3 py-1.5 rounded transition-colors duration-200 flex items-center space-x-1 flex-shrink-0"
        >
          <span>Cómpralo</span>
          <ExternalLink className="h-3 w-3" />
        </a>
      </div>
    </div>
  );
}

// Grid layout for multiple products
export function AmazonProductGrid({ 
  products, 
  columns = 4, 
  size = 'medium',
  className = '' 
}: {
  products: AmazonProductCardProps['product'][];
  columns?: 2 | 3 | 4;
  size?: 'small' | 'medium' | 'large';
  className?: string;
}) {
  const getGridClasses = () => {
    switch (columns) {
      case 2:
        return 'grid-cols-1 md:grid-cols-2';
      case 3:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
      case 4:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';
      default:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';
    }
  };

  if (products.length === 0) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <ShoppingCart className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-muted-foreground">No hay productos disponibles</p>
      </div>
    );
  }

  return (
    <div className={`amazon-product-grid grid gap-4 ${getGridClasses()} ${className}`}>
      {products.map((product) => (
        <AmazonProductCard
          key={product.id}
          product={product}
          size={size}
        />
      ))}
    </div>
  );
}

export default AmazonProductCard;
